import { buttonClick, pageview } from '@wix/bi-logger-vmaf/v2';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ErrorType } from '../../api/ErrorNormalizer';
import biLogger from '../../lib/bi/biLogger';
import * as videoInfoSelectors from '../../redux/videoInfo/selectors';
import { VideoType } from '../../redux/videoInfo/types';
import compose from '../../utils/compose';
import ErrorOverlayBase from './ErrorOverlayBase';

interface IErrorTextKeys {
  action: string;
  title: string;
  subtitle: string;
}

const ERROR_TEXT_KEYS: Record<string, IErrorTextKeys> = {
  [ErrorType.UNAUTHORIZED]: {
    action: 'errors.error-overlay.auth.action',
    title: 'errors.error-overlay.auth.title',
    subtitle: 'errors.error-overlay.auth.subtitle',
  },
  [ErrorType.MEDIA]: {
    action: 'errors.error-overlay.invalid-files.action',
    title: 'errors.error-overlay.invalid-files.title',
    subtitle: 'errors.error-overlay.invalid-files.subtitle',
  },
  [ErrorType.UNKNOWN]: {
    action: 'errors.error-overlay.unknown.action',
    title: 'errors.error-overlay.unknown.title',
    subtitle: 'errors.error-overlay.unknown.subtitle',
  },
  [ErrorType.SITE_UNKNOWN]: {
    action: 'errors.error-overlay.unknown.action',
    title: 'errors.error-overlay.unknown.title',
    subtitle: 'errors.error-overlay.site-unknown.subtitle',
  },
};

type IErrorOverlayAction = (event: React.MouseEvent<HTMLButtonElement>) => void;

interface IStoreProps {
  videoType: VideoType;
}

const mapStateToProps = (state) => ({
  videoType: videoInfoSelectors.getVideoType(state),
});

interface IErrorOverlayActions {
  onAuthorize?: IErrorOverlayAction;
  onEditMedia?: IErrorOverlayAction;
  onTryAgain?: IErrorOverlayAction;
}

interface IErrorOverlayProps
  extends IErrorOverlayActions,
    IStoreProps,
    WithTranslation {
  errorType: ErrorType;
}

class ErrorOverlay extends React.Component<IErrorOverlayProps> {
  ERROR_ACTION_MAP = {
    [ErrorType.UNAUTHORIZED]: (e) => this.props.onAuthorize(e),
    [ErrorType.MEDIA]: (e) => this.props.onEditMedia(e),
    [ErrorType.UNKNOWN]: (e) => this.props.onTryAgain(e),
    [ErrorType.SITE_UNKNOWN]: (e) => this.props.onTryAgain(e),
  };
  ERROR_BI_MAP = {
    [ErrorType.UNAUTHORIZED]: 'error_session_expired',
    [ErrorType.MEDIA]: 'error_choose_another_media',
    [ErrorType.UNKNOWN]: 'error_creation',
  };

  componentDidMount() {
    const biData = this.getBiData();

    void biLogger.report(pageview(biData));
  }

  getBiData() {
    const { errorType, videoType } = this.props;
    const pageName =
      this.ERROR_BI_MAP[errorType] || this.ERROR_BI_MAP[ErrorType.UNKNOWN];

    return {
      pageName,
      type: videoType,
    };
  }

  logBiMain = () => {
    const biData = this.getBiData();

    void biLogger.report(
      buttonClick({
        ...biData,
        buttonName: 'main',
      }),
    );
  };

  getErrorInfo(errorType: ErrorType): {
    textKeys: IErrorTextKeys;
    action(event: React.MouseEvent<HTMLButtonElement>): void;
  } {
    return {
      textKeys:
        ERROR_TEXT_KEYS[errorType] || ERROR_TEXT_KEYS[ErrorType.UNKNOWN],
      action:
        this.ERROR_ACTION_MAP[errorType] ||
        this.ERROR_ACTION_MAP[ErrorType.UNKNOWN],
    };
  }

  render() {
    const { t, errorType } = this.props;
    const { textKeys, action } = this.getErrorInfo(errorType);

    if (!action) {
      throw new Error(`Action not provided for error type ${errorType}`);
    }

    return (
      <ErrorOverlayBase
        title={t(textKeys.title)}
        subtitle={t(textKeys.subtitle)}
        actionLabel={t(textKeys.action)}
        onAction={(e) => {
          this.logBiMain();
          action(e);
        }}
      />
    );
  }
}

export default compose(
  withTranslation(),
  connect(mapStateToProps),
)(ErrorOverlay);
